import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Button, Stack, Avatar, IconButton, Tooltip, TextField, InputAdornment,
    Drawer, Link, CircularProgress,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import HideImageRoundedIcon from '@mui/icons-material/HideImageRounded';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PersonOffRoundedIcon from '@mui/icons-material/PersonOffRounded';
import { PRIMARY, PRIMARY_LIGHT } from '../../theme';
import { fmtDayDate, fmtMinutes, initialsFromName as initials, avatarTone } from '../../utils/format';
import { fetchFileBlob } from '../../utils/download';
import { ghostBtn, field, card, EmptyState, SkeletonRows } from '../uiKit';
import { INK, MUTED, LINE, LINE_STRONG, HOVER, RADIUS, Segmented, Label } from './punchUi';
import http, { apiErrorMessage } from '../../Api/http';
import {
    GetSelfPunches, GetSelfPunchDetail, GetSelfPunchSummary,
    GetEmployeesWithoutCheckIn, DownloadSelfPunchPhoto,
} from '../../Api/Api';

const COLS = 'minmax(190px, 1.4fr) 120px 200px 200px 90px 100px';
const MIN_TABLE_WIDTH = 1000;
const PAD_X = 2.5;
const PAGE_SIZE = 50;

const pad = (n) => String(n).padStart(2, '0');
const toInputDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
// Native date inputs give yyyy-MM-dd; this endpoint family wants dd-MM-yyyy.
const toApiDate = (iso) => (iso ? iso.split('-').reverse().join('-') : undefined);

const SOURCE_LABEL = { self: 'App', biometric: 'Biometric', manual: 'Manual' };

// Photos sit behind the bearer token, so an <img src> can't load them. Each is
// fetched once as a blob and kept as an object URL for the session.
const photoCache = new Map();
const loadPhoto = (employeeCode, date, side) => {
    const key = `${employeeCode}|${date}|${side}`;
    if (!photoCache.has(key)) {
        const p = fetchFileBlob(DownloadSelfPunchPhoto, { employeeCode, date, side })
            .then(({ blob }) => URL.createObjectURL(blob))
            .catch(() => { photoCache.delete(key); return null; });
        photoCache.set(key, p);
    }
    return photoCache.get(key);
};

function Photo({ employeeCode, date, side, hasPhoto, size = 40, onOpen, fit = 'cover' }) {
    const ref = useRef(null);
    const [src, setSrc] = useState(null);
    const [state, setState] = useState(hasPhoto ? 'idle' : 'none');

    useEffect(() => {
        if (!hasPhoto) { setState('none'); return undefined; }
        const node = ref.current;
        let alive = true;
        const start = () => {
            setState('loading');
            loadPhoto(employeeCode, date, side).then((u) => {
                if (!alive) return;
                if (u) { setSrc(u); setState('ready'); } else setState('none');
            });
        };
        if (typeof IntersectionObserver === 'undefined' || !node) { start(); return () => { alive = false; }; }
        const io = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) { io.disconnect(); start(); }
        });
        io.observe(node);
        return () => { alive = false; io.disconnect(); };
    }, [employeeCode, date, side, hasPhoto]);

    const clickable = state === 'ready' && onOpen;
    return (
        <Box
            ref={ref}
            onClick={clickable ? onOpen : undefined}
            sx={{
                width: size, height: size, borderRadius: RADIUS, flexShrink: 0, overflow: 'hidden',
                bgcolor: '#F3F7FA', border: `1px solid ${LINE_STRONG}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: clickable ? 'zoom-in' : 'default',
            }}
        >
            {state === 'ready' ? (
                <Box component="img" src={src} alt={`${side === 'in' ? 'Check-in' : 'Check-out'} photo`}
                    sx={{ width: '100%', height: '100%', objectFit: fit }} />
            ) : state === 'loading' ? (
                <CircularProgress size={Math.max(14, size / 3)} thickness={5} sx={{ color: MUTED }} />
            ) : (
                <HideImageRoundedIcon sx={{ fontSize: size / 2, color: '#CBD2DD' }} />
            )}
        </Box>
    );
}

function MapPin({ url }) {
    if (!url) return null;
    return (
        <Tooltip arrow title="Open location in Google Maps">
            <IconButton
                component="a" href={url} target="_blank" rel="noopener noreferrer" size="small"
                sx={{ width: 28, height: 28, color: PRIMARY, bgcolor: PRIMARY_LIGHT, '&:hover': { bgcolor: '#D0E5F3' } }}
            >
                <PlaceRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
        </Tooltip>
    );
}

function PunchCell({ row, side }) {
    const inn = side === 'in';
    const time = inn ? row.checkInTime : row.checkOutTime;
    const photoUrl = inn ? row.checkInPhotoUrl : row.checkOutPhotoUrl;
    const mapUrl = inn ? row.checkInMapUrl : row.checkOutMapUrl;
    const source = inn ? row.checkInSource : row.checkOutSource;
    return (
        <Stack direction="row" spacing={1.1} sx={{ alignItems: 'center', minWidth: 0 }}>
            <Photo employeeCode={row.employeeCode} date={row.date} side={side} hasPhoto={Boolean(photoUrl)} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: time ? INK : MUTED, fontVariantNumeric: 'tabular-nums' }} noWrap>
                    {time || '—'}
                </Typography>
                <Typography sx={{ fontSize: 11, color: MUTED }} noWrap>{SOURCE_LABEL[source] || (time ? '' : 'Not recorded')}</Typography>
            </Box>
            <MapPin url={mapUrl} />
        </Stack>
    );
}

function StatusBadge({ open }) {
    const c = open ? { fg: '#B45309', bg: '#FFF7ED', bd: '#F5D9B3', label: 'Open' } : { fg: '#15803D', bg: '#F0FDF4', bd: '#C4EBD1', label: 'Closed' };
    return (
        <Box sx={{ display: 'inline-flex', px: 1.1, py: 0.35, borderRadius: '999px', bgcolor: c.bg, border: `1px solid ${c.bd}` }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: c.fg }}>{c.label}</Typography>
        </Box>
    );
}

function SummaryStrip({ items }) {
    return (
        <Box sx={{ ...card, mb: 2, overflow: 'hidden', display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
            {items.map((it, i) => (
                <Stack
                    key={it.label} direction="row" spacing={1.4}
                    sx={{
                        alignItems: 'center', px: 2.2, py: 1.8, minWidth: 0,
                        borderLeft: { xs: i % 2 ? `1px solid ${LINE}` : 'none', md: i ? `1px solid ${LINE}` : 'none' },
                        borderTop: { xs: i >= 2 ? `1px solid ${LINE}` : 'none', md: 'none' },
                    }}
                >
                    <Box sx={{ width: 40, height: 40, borderRadius: '10px', flexShrink: 0, bgcolor: it.bg, border: `1px solid ${it.color}26`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <it.icon sx={{ fontSize: 19, color: it.color }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, color: INK, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>{it.value}</Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#5B6472' }} noWrap>{it.label}</Typography>
                        <Typography sx={{ fontSize: 11, color: MUTED }} noWrap>{it.sub}</Typography>
                    </Box>
                </Stack>
            ))}
        </Box>
    );
}

function DetailDrawer({ target, onClose }) {
    const [detail, setDetail] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [zoom, setZoom] = useState(null);

    useEffect(() => {
        if (!target) return undefined;
        let alive = true;
        setDetail(null); setError(''); setLoading(true);
        http.get(GetSelfPunchDetail, { params: { employeeCode: target.employeeCode, date: target.date } })
            .then(({ data: body }) => {
                if (!alive) return;
                if (body?.error) throw new Error(body.message || 'Could not load this day.');
                setDetail(body?.data || null);
            })
            .catch((err) => { if (alive) setError(apiErrorMessage(err, 'Could not load this day.')); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [target]);

    const card0 = detail?.card || target;
    const audit = detail?.audit || [];

    const Side = ({ side }) => {
        const inn = side === 'in';
        const time = inn ? card0?.checkInTime : card0?.checkOutTime;
        const photo = inn ? card0?.checkInPhotoUrl : card0?.checkOutPhotoUrl;
        const map = inn ? card0?.checkInMapUrl : card0?.checkOutMapUrl;
        const lat = inn ? card0?.checkInLatitude : card0?.checkOutLatitude;
        const lng = inn ? card0?.checkInLongitude : card0?.checkOutLongitude;
        const source = inn ? card0?.checkInSource : card0?.checkOutSource;
        return (
            <Box sx={{ flex: 1, minWidth: 0, border: `1px solid ${LINE}`, borderRadius: RADIUS, p: 1.4 }}>
                <Stack direction="row" spacing={0.7} sx={{ alignItems: 'center', mb: 1 }}>
                    {inn ? <LoginRoundedIcon sx={{ fontSize: 16, color: '#16A34A' }} /> : <LogoutRoundedIcon sx={{ fontSize: 16, color: '#0369A1' }} />}
                    <Label>{inn ? 'Check-in' : 'Check-out'}</Label>
                </Stack>
                <Photo
                    employeeCode={card0?.employeeCode} date={card0?.date} side={side}
                    hasPhoto={Boolean(photo)} size={170} fit="contain"
                    onOpen={() => loadPhoto(card0.employeeCode, card0.date, side).then((u) => u && setZoom(u))}
                />
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: time ? INK : MUTED, mt: 1, fontVariantNumeric: 'tabular-nums' }}>{time || '—'}</Typography>
                <Typography sx={{ fontSize: 11.5, color: MUTED }}>{SOURCE_LABEL[source] || (time ? '' : 'Not recorded')}</Typography>
                {map && (
                    <Link href={map} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 0.4, mt: 0.6 }}>
                        <PlaceRoundedIcon sx={{ fontSize: 14 }} />
                        {Number.isFinite(lat) && Number.isFinite(lng) ? `${lat.toFixed(5)}, ${lng.toFixed(5)}` : 'View on map'}
                    </Link>
                )}
            </Box>
        );
    };

    return (
        <Drawer anchor="right" open={Boolean(target)} onClose={onClose} slotProps={{ paper: { sx: { width: { xs: '100%', sm: 520 } } } }}>
            <Stack direction="row" sx={{ alignItems: 'center', px: 2.5, py: 2, borderBottom: `1px solid ${LINE_STRONG}` }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 16.5, fontWeight: 800, color: INK }} noWrap>{card0?.employeeName || '—'}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280' }} noWrap>
                        {[card0?.companyEmployeeId || card0?.employeeCode, card0?.department, fmtDayDate(card0?.date)].filter(Boolean).join(' · ')}
                    </Typography>
                </Box>
                <IconButton onClick={onClose}><CloseRoundedIcon /></IconButton>
            </Stack>

            <Box sx={{ p: 2.5, overflowY: 'auto' }}>
                {loading && <SkeletonRows count={3} />}
                {!loading && error && (
                    <Typography sx={{ fontSize: 13, color: '#BE123C', mb: 2 }}>{error}</Typography>
                )}
                {!loading && !error && (
                    <>
                        <Stack direction="row" spacing={1.5} sx={{ mb: 2.5 }}>
                            <Side side="in" />
                            <Side side="out" />
                        </Stack>

                        <Label sx={{ mb: 1 }}>Audit trail</Label>
                        {audit.length === 0 ? (
                            <Typography sx={{ fontSize: 12.5, color: MUTED }}>No changes recorded for this day.</Typography>
                        ) : audit.map((a, i) => (
                            <Box key={i} sx={{ py: 1.1, borderBottom: i < audit.length - 1 ? `1px solid ${LINE}` : 'none' }}>
                                <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK }}>
                                        {a.field}: {a.oldValue ?? '—'} → {a.newValue ?? '—'}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11.5, color: MUTED, flexShrink: 0 }}>{a.changedAt}</Typography>
                                </Stack>
                                <Typography sx={{ fontSize: 11.5, color: MUTED }}>
                                    {[SOURCE_LABEL[a.source] || a.source, a.action, a.changedBy && `by ${a.changedBy}`].filter(Boolean).join(' · ')}
                                </Typography>
                                {a.reason && <Typography sx={{ fontSize: 12, color: '#475569', mt: 0.3 }}>“{a.reason}”</Typography>}
                            </Box>
                        ))}
                    </>
                )}
            </Box>

            {zoom && (
                <Box
                    onClick={() => setZoom(null)}
                    sx={{ position: 'fixed', inset: 0, zIndex: 1500, bgcolor: 'rgba(15,23,42,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', p: 2 }}
                >
                    <Box component="img" src={zoom} alt="Punch photo" sx={{ maxWidth: '100%', maxHeight: '100%', borderRadius: RADIUS }} />
                </Box>
            )}
        </Drawer>
    );
}

export default function AppCheckInsTab() {
    const today = useMemo(() => new Date(), []);
    const [view, setView] = useState('punches');
    const [status, setStatus] = useState('all');
    const [fromDate, setFromDate] = useState(toInputDate(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [toDate, setToDate] = useState(toInputDate(today));
    const [department, setDepartment] = useState('');
    const [q, setQ] = useState('');
    const [page, setPage] = useState(1);

    const [data, setData] = useState({ total: 0, open: 0, closed: 0, items: [] });
    const [missing, setMissing] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [selected, setSelected] = useState(null);

    // Search is applied on the server; wait for a pause so each keystroke isn't a request.
    const [qDebounced, setQDebounced] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setQDebounced(q.trim()), 350);
        return () => clearTimeout(t);
    }, [q]);
    useEffect(() => { setPage(1); }, [status, fromDate, toDate, department, qDebounced]);

    const loadSummary = useCallback(async () => {
        try {
            const { data: body } = await http.get(GetSelfPunchSummary);
            if (!body?.error) setSummary(body?.data || null);
        } catch { setSummary(null); }
    }, []);
    useEffect(() => { loadSummary(); }, [loadSummary]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            if (view === 'punches') {
                const { data: body } = await http.get(GetSelfPunches, {
                    params: {
                        fromDate: toApiDate(fromDate), toDate: toApiDate(toDate), status,
                        department: department.trim() || undefined, search: qDebounced || undefined,
                        page, pageSize: PAGE_SIZE,
                    },
                });
                if (body?.error) throw new Error(body.message || 'Could not load app check-ins.');
                const d = body?.data || {};
                setData({ total: d.total ?? 0, open: d.open ?? 0, closed: d.closed ?? 0, items: d.items || [] });
            } else {
                const { data: body } = await http.get(GetEmployeesWithoutCheckIn);
                if (body?.error) throw new Error(body.message || 'Could not load the list.');
                setMissing(body?.data?.items || []);
            }
            setLoadError('');
        } catch (err) {
            // A list with no matches is a 404 by house convention — that is "no data", not a failure.
            if (err?.response?.status === 404) {
                if (view === 'punches') setData({ total: 0, open: 0, closed: 0, items: [] }); else setMissing([]);
                setLoadError('');
            } else {
                setLoadError(apiErrorMessage(err, 'Could not load app check-ins.'));
            }
        } finally {
            setLoading(false);
        }
    }, [view, status, fromDate, toDate, department, qDebounced, page]);
    useEffect(() => { load(); }, [load]);

    const missingShown = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return missing;
        return missing.filter((m) => [m.employeeName, m.employeeCode, m.companyEmployeeId, m.department]
            .some((v) => String(v || '').toLowerCase().includes(s)));
    }, [missing, q]);

    const KPIS = [
        { label: 'Eligible today', value: summary?.eligible ?? '—', sub: `${summary?.hybrid ?? 0} hybrid · ${summary?.workFromHome ?? 0} WFH`, icon: GroupsRoundedIcon, color: PRIMARY, bg: PRIMARY_LIGHT },
        { label: 'Checked in', value: summary?.checkedIn ?? '—', sub: `${summary?.checkedOut ?? 0} checked out`, icon: LoginRoundedIcon, color: '#16A34A', bg: '#DCFCE7' },
        { label: 'Still open', value: summary?.open ?? '—', sub: 'No check-out yet', icon: HourglassTopRoundedIcon, color: '#B45309', bg: '#FFF7ED' },
        { label: 'Not checked in', value: summary?.notCheckedIn ?? '—', sub: 'Follow up', icon: PersonOffRoundedIcon, color: '#E11D48', bg: '#FEE2E2' },
    ];

    const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
    const dateInput = {
        ...field, width: 138,
        '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 38, fontSize: 13 },
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ maxWidth: 760, pb: 2.2 }}>
                <Typography sx={{ fontSize: 19, fontWeight: 800, color: '#111827', letterSpacing: '-0.3px' }}>App Check-ins</Typography>
                <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.3, lineHeight: 1.55 }}>
                    Check-ins and check-outs made from the mobile app, with the live photo and the position the phone reported.
                    Location is recorded as reported — it is not checked against a site radius.
                </Typography>
            </Box>

            <SummaryStrip items={KPIS} />

            <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1.2, px: PAD_X, py: 1.6, borderBottom: `1px solid ${LINE_STRONG}`, flexWrap: 'wrap' }}>
                    <Segmented
                        items={[{ key: 'punches', label: 'Check-ins' }, { key: 'missing', label: 'Not checked in today', count: summary?.notCheckedIn }]}
                        value={view} onChange={setView}
                    />
                    {view === 'punches' && (
                        <Segmented
                            items={[
                                { key: 'all', label: 'All', count: data.open + data.closed },
                                { key: 'open', label: 'Open', count: data.open },
                                { key: 'closed', label: 'Closed', count: data.closed },
                            ]}
                            value={status} onChange={setStatus}
                        />
                    )}
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', ml: 'auto', flexWrap: { xs: 'wrap', lg: 'nowrap' }, justifyContent: 'flex-end' }}>
                        {view === 'punches' && (
                            <>
                                <TextField size="small" type="date" label="From" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: toDate } }} sx={dateInput} />
                                <TextField size="small" type="date" label="To" value={toDate} onChange={(e) => setToDate(e.target.value)}
                                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: fromDate } }} sx={dateInput} />
                                <TextField size="small" placeholder="Department" value={department} onChange={(e) => setDepartment(e.target.value)}
                                    sx={{ ...dateInput, width: 120 }} />
                            </>
                        )}
                        <TextField
                            size="small" placeholder="Search name or ID" value={q} onChange={(e) => setQ(e.target.value)}
                            sx={{ ...field, width: { xs: '100%', sm: 170 }, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 38, fontSize: 13 } }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18, color: MUTED }} /></InputAdornment> }}
                        />
                        <Tooltip arrow title="Reload"><span>
                            <IconButton onClick={() => { load(); loadSummary(); }} disabled={loading} sx={{ ...ghostBtn, width: 38, height: 38 }}>
                                <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </span></Tooltip>
                    </Stack>
                </Stack>

                {loading ? (
                    <SkeletonRows count={5} />
                ) : loadError ? (
                    <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#64748B' }}>Couldn’t load app check-ins</Typography>
                        <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.4 }}>{loadError}</Typography>
                        <Button onClick={() => load()} sx={{ ...ghostBtn, height: 34, px: 2, mt: 1.8 }}>Try again</Button>
                    </Box>
                ) : view === 'punches' ? (
                    data.items.length === 0 ? (
                        <EmptyState
                            icon={PhotoCameraRoundedIcon}
                            title="No app check-ins in this range"
                            hint="Days appear here once an employee checks in from the mobile app. Biometric-only days are on the Attendance tab."
                        />
                    ) : (
                        <>
                            <Box sx={{ overflowX: 'auto' }}>
                                <Box sx={{ minWidth: MIN_TABLE_WIDTH }}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: COLS, alignItems: 'center', gap: 2, px: PAD_X, py: 1.3, bgcolor: '#FAFAFD', borderBottom: `1px solid ${LINE_STRONG}` }}>
                                        <Label>Employee</Label><Label>Date</Label><Label>Check-in</Label><Label>Check-out</Label>
                                        <Label>Worked</Label><Label sx={{ textAlign: 'right' }}>Status</Label>
                                    </Box>
                                    {data.items.map((r) => (
                                        <Box
                                            key={`${r.employeeCode}-${r.date}`} onClick={() => setSelected(r)}
                                            sx={{
                                                display: 'grid', gridTemplateColumns: COLS, alignItems: 'center', gap: 2,
                                                px: PAD_X, py: 1.5, borderBottom: `1px solid ${LINE}`, cursor: 'pointer',
                                                '&:hover': { bgcolor: HOVER }, '&:last-of-type': { borderBottom: 'none' },
                                            }}
                                        >
                                            <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center', minWidth: 0 }}>
                                                <Avatar sx={{ width: 36, height: 36, ...avatarTone(r.employeeName), fontSize: 12.5, fontWeight: 700, flexShrink: 0 }}>{initials(r.employeeName)}</Avatar>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }} noWrap>{r.employeeName || '—'}</Typography>
                                                    <Typography sx={{ fontSize: 11.5, color: MUTED }} noWrap>
                                                        {[r.companyEmployeeId || r.employeeCode, r.department].filter(Boolean).join(' · ')}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: INK, fontVariantNumeric: 'tabular-nums' }} noWrap>{r.date}</Typography>
                                            <PunchCell row={r} side="in" />
                                            <PunchCell row={r} side="out" />
                                            <Typography sx={{ fontSize: 13, color: INK, fontVariantNumeric: 'tabular-nums' }}>{fmtMinutes(r.workedMinutes) || '—'}</Typography>
                                            <Box sx={{ textAlign: 'right' }}><StatusBadge open={r.isOpen} /></Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: PAD_X, py: 1.4, borderTop: `1px solid ${LINE_STRONG}` }}>
                                <Typography sx={{ fontSize: 12.5, color: MUTED }}>{data.total} day{data.total === 1 ? '' : 's'} · page {page} of {pages}</Typography>
                                <Stack direction="row" spacing={1}>
                                    <Button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} sx={{ ...ghostBtn, height: 32, px: 1.6 }}>Previous</Button>
                                    <Button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} sx={{ ...ghostBtn, height: 32, px: 1.6 }}>Next</Button>
                                </Stack>
                            </Stack>
                        </>
                    )
                ) : missingShown.length === 0 ? (
                    <EmptyState
                        icon={q ? InboxRoundedIcon : PhotoCameraRoundedIcon}
                        title={q ? 'Nothing matches that search' : 'Everyone eligible has checked in'}
                        hint={q ? 'Try another name or ID.' : 'Employees on hybrid or work-from-home roles with an active shift show here until they check in.'}
                    />
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ minWidth: 760 }}>
                            {missingShown.map((m) => (
                                <Stack
                                    key={m.employeeCode} direction="row" spacing={1.3}
                                    sx={{ alignItems: 'center', px: PAD_X, py: 1.5, borderBottom: `1px solid ${LINE}`, '&:hover': { bgcolor: HOVER }, '&:last-of-type': { borderBottom: 'none' } }}
                                >
                                    <Avatar sx={{ width: 36, height: 36, ...avatarTone(m.employeeName), fontSize: 12.5, fontWeight: 700 }}>{initials(m.employeeName)}</Avatar>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }} noWrap>{m.employeeName}</Typography>
                                        <Typography sx={{ fontSize: 11.5, color: MUTED }} noWrap>
                                            {[m.companyEmployeeId || m.employeeCode, m.department, m.designation].filter(Boolean).join(' · ')}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: 12, color: MUTED, textTransform: 'capitalize', width: 70 }}>{m.attendanceMode}</Typography>
                                    {m.personalMobile ? (
                                        <Button
                                            component="a" href={`tel:${m.personalMobile}`}
                                            startIcon={<PhoneRoundedIcon sx={{ fontSize: 16 }} />}
                                            sx={{ ...ghostBtn, height: 32, px: 1.4, fontSize: 12.5 }}
                                        >
                                            {m.personalMobile}
                                        </Button>
                                    ) : (
                                        <Typography sx={{ fontSize: 12, color: MUTED, width: 130, textAlign: 'right' }}>No mobile on file</Typography>
                                    )}
                                </Stack>
                            ))}
                        </Box>
                    </Box>
                )}
            </Box>

            <DetailDrawer target={selected} onClose={() => setSelected(null)} />
        </Box>
    );
}
